import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RotateCcw } from 'lucide-react'

// The Ask-AI dock is mounted in the sidebar with no boundary above it, so a
// render error anywhere inside it unmounted the whole application — a blank
// white page, with the agent's question and the conversation gone from view.
// This keeps the failure to the panel: the conversation and any run are on the
// server, so "Reload panel" (a fresh mount, which re-reads them) loses nothing.

interface Props { project: string; children: ReactNode }
interface State { error: Error | null; generation: number }

export default class DockErrorBoundary extends Component<Props, State> {
  state: State = { error: null, generation: 0 }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(`[Ask-AI dock "${this.props.project}"] render failed:`, error, info.componentStack)
  }

  componentDidUpdate(prev: Props) {
    // Another project's dock is a different panel — give it a clean start.
    if (prev.project !== this.props.project && this.state.error) {
      this.setState((s) => ({ error: null, generation: s.generation + 1 }))
    }
  }

  private reload = () => {
    this.setState((s) => ({ error: null, generation: s.generation + 1 }))
  }

  render() {
    if (this.state.error) {
      return (
        <div
          role="alert"
          style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: '0.75rem', padding: '1.5rem 1rem', textAlign: 'center',
            color: 'var(--color-text)', background: 'var(--color-bg-secondary)',
          }}
        >
          <AlertTriangle size={28} color="var(--color-warning)" aria-hidden="true" />
          <div style={{ fontWeight: 600, fontSize: '1rem' }}>The assistant panel hit an error</div>
          <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', lineHeight: 1.45 }}>
            Your conversation and any build in progress are safe on the server.
            Reload the panel to pick up where you left off.
          </div>
          <button
            type="button"
            onClick={this.reload}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
              padding: '0.5rem 0.875rem', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-accent)', background: 'var(--color-accent)',
              color: '#fff', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
            }}
          >
            <RotateCcw size={14} aria-hidden="true" /> Reload panel
          </button>
          <code
            style={{
              fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
              maxWidth: '100%', overflowWrap: 'anywhere',
            }}
          >
            {this.state.error.message}
          </code>
        </div>
      )
    }
    // Keyed by generation: Reload mounts the dock afresh.
    return <div key={this.state.generation} style={{ display: 'contents' }}>{this.props.children}</div>
  }
}
